import React, { useEffect, useState } from 'react';
import { addFormatApi, getAllLrFormateApi } from '_services/accountsServices/branchServices';
import { CommonToast, CrossButton } from 'components';
import Loading from 'pages/Loading';
import { AppConfig } from 'lib';
import toast from 'react-hot-toast';

const CrossIcon = () => (
    <svg className="cross" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
        <path
            d="M12.5321 0.477671C12.4422 0.387555 12.3353 0.316061 12.2177 0.267281C12.1001 0.2185 11.9741 0.193391 11.8468 0.193391C11.7195 0.193391 11.5934 0.2185 11.4758 0.267281C11.3582 0.316061 11.2514 0.387555 11.1615 0.477671L6.40798 5.22143L1.6545 0.46795C1.56451 0.377952 1.45766 0.306563 1.34008 0.257857C1.22249 0.20915 1.09646 0.184082 0.969185 0.184082C0.84191 0.184082 0.715881 0.20915 0.598294 0.257857C0.480707 0.306563 0.373865 0.377952 0.283868 0.46795C0.19387 0.557947 0.122481 0.664789 0.0737746 0.782376C0.0250685 0.899963 -9.48274e-10 1.02599 0 1.15327C9.48275e-10 1.28054 0.0250684 1.40657 0.0737746 1.52416C0.122481 1.64175 0.19387 1.74859 0.283868 1.83858L5.03735 6.59206L0.283868 11.3455C0.19387 11.4355 0.122481 11.5424 0.0737746 11.66C0.0250685 11.7776 0 11.9036 0 12.0309C0 12.1581 0.0250685 12.2842 0.0737746 12.4017C0.122481 12.5193 0.19387 12.6262 0.283868 12.7162C0.373865 12.8062 0.480707 12.8776 0.598294 12.9263C0.715881 12.975 0.84191 13 0.969185 13C1.09646 13 1.22249 12.975 1.34008 12.9263C1.45766 12.8776 1.56451 12.8062 1.6545 12.7162L6.40798 7.9627L11.1615 12.7162C11.2515 12.8062 11.3583 12.8776 11.4759 12.9263C11.5935 12.975 11.7195 13 11.8468 13C11.974 13 12.1001 12.975 12.2177 12.9263C12.3353 12.8776 12.4421 12.8062 12.5321 12.7162C12.6221 12.6262 12.6935 12.5193 12.7422 12.4017C12.7909 12.2842 12.816 12.1581 12.816 12.0309C12.816 11.9036 12.7909 11.7776 12.7422 11.66C12.6935 11.5424 12.6221 11.4355 12.5321 11.3455L7.77861 6.59206L12.5321 1.83858C12.9015 1.46919 12.9015 0.847062 12.5321 0.477671Z"
            fill="black"
        />
    </svg>
);

/**
 * Props:
 * @param {boolean}  show          - controls offcanvas visibility
 * @param {function} onClose       - called when the panel is closed
 * @param {string}   companyId     - current company id
 * @param {string}   branchId      - current branch id
 * @param {Array}    addedFormats  - formats already added to the branch (to mark as "Added")
 * @param {function} onFormatAdded - callback fired after a format is successfully added;
 *                                   receives the updated addedFormats array
 */
const AddLrFormatModal = ({
    show,
    onClose,
    companyId,
    branchId,
    addedFormats = [],
    onFormatAdded,
}) => {
    const BASE_URL = AppConfig.api_baseurl;

    const [formatList, setFormatList] = useState([]);
    const [isLrLoading, setIsLrLoading] = useState(false);
    const [loadingId, setLoadingId] = useState(null);
    const [selectedFormat, setSelectedFormat] = useState(null);
    // local copy so "Added" state updates immediately without waiting for parent refetch
    const [localAdded, setLocalAdded] = useState(addedFormats);

    // Sync localAdded whenever parent passes a fresh addedFormats list
    useEffect(() => {
        setLocalAdded(addedFormats);
    }, [addedFormats]);

    // Fetch all available LR formats whenever the panel opens
    useEffect(() => {
        if (show) {
            fetchAllFormats();
        }
    }, [show]);

    const fetchAllFormats = async () => {
        setIsLrLoading(true);
        try {
            const res = await getAllLrFormateApi({ company_id: companyId });
            if (res?.responseCode === 200) {
                const withBlobs = await Promise.all(
                    res.data.formats.map(async (rf) => {
                        const response = await fetch(`${BASE_URL}${rf.template_img}`);
                        const blob = await response.blob();
                        return { ...rf, imageUrl: URL.createObjectURL(blob) };
                    })
                );
                setFormatList(withBlobs);
            }
        } catch (err) {
            console.error('Failed to load LR formats', err);
        } finally {
            setIsLrLoading(false);
        }
    };

    const handleAddFormat = async (fr) => {
        const frWithoutImage = { ...fr };
        delete frWithoutImage.imageUrl;

        const payload = {
            company_id: companyId,
            branch_id: branchId,
            format: frWithoutImage,
        };

        setLoadingId(fr.id);
        try {
            const res = await addFormatApi(payload);
            if (res?.responseCode === 200) {
                toast.success(res?.responseMessage || 'Added Successfully');

                const updated = [...localAdded, frWithoutImage];
                setLocalAdded(updated);
               
                onFormatAdded?.({ localAdded: updated, frWithoutImage });
            } else {
                CommonToast(res?.responseMessage);
            }
        } catch (err) {
            console.error('Add format error', err);
        } finally {
            setLoadingId(null);
        }
    };

    const isAdded = (fr) => localAdded?.some((f) => f?.id === fr?.id);
    const isLoading = (fr) => loadingId === fr?.id;

    return (
        <>
            {/* ── Offcanvas ── */}
            <div
                className={`offcanvas offcanvas-end common_offCanvas xxlOffcanvas ${show ? 'show' : ''}`}
                tabIndex={-1}
                style={{ visibility: show ? 'visible' : 'hidden' }}
            >
                {/* Header */}
                <div className="offcanvas-header">
                    <h5 className="offcanvas-title">Add LR Format</h5>
                    <CrossButton
                        type="button"
                        className="_switchBtn __animEffect_btn"
                        onClick={onClose}
                        style={{ marginLeft: 'auto' }}
                    >
                        <CrossIcon />
                    </CrossButton>
                </div>

                {/* Body */}
                <div className="offcanvas-body" style={{ height: 'calc(100vh - 109px)' }}>
                    <div className="__layOutFr_body" style={{ height: isLrLoading ? '100%' : 'auto' }}>
                        <div
                            className="__inrFormate_main"
                            style={{ position: 'relative', height: isLrLoading ? '100%' : 'auto' }}
                        >
                            <div className="__frmInner_fx">

                                {/* Loading state */}
                                {isLrLoading && (
                                    <div colSpan="100%" className="loaderBg" style={{ textAlign: 'center' }}>
                                        <Loading />
                                    </div>
                                )}

                                {/* Format cards */}
                                {!isLrLoading &&
                                    formatList.map((fr) => (
                                        <div className="__frmOtr_cover" key={fr.id}>
                                            <div className="__frm_coverInr">

                                                {/* Thumbnail – opens preview */}
                                                <div
                                                    className="__frm_container position-relative"
                                                    style={{ minHeight: '200px', cursor: 'pointer' }}
                                                    onClick={() => setSelectedFormat(fr)}
                                                >
                                                    <img src={fr.imageUrl} alt={fr.name} style={{ width: '100%' }} />
                                                </div>

                                                {/* Add button */}
                                                <div className="__frm_setting_sd">
                                                    <button
                                                        className="_add_lrForm_btn"
                                                        style={{
                                                            backgroundColor: isAdded(fr) ? '#ccc' : 'rgba(0, 21, 60, 1)',
                                                            color: isAdded(fr) ? '#666' : '#fff',
                                                            cursor: isAdded(fr) ? 'not-allowed' : 'pointer',
                                                            border: 'none',
                                                            padding: '8px 16px',
                                                            transition: 'background-color 0.3s',
                                                        }}
                                                        disabled={isAdded(fr) || isLoading(fr)}
                                                        onClick={() => handleAddFormat(fr)}
                                                    >
                                                        {isLoading(fr) ? 'Adding…' : isAdded(fr) ? 'Added' : 'Add Format'}
                                                    </button>
                                                </div>
                                            </div>

                                            <p className="_frm_txt">{fr.name}</p>
                                        </div>
                                    ))
                                }

                                {/* ── Preview modal (inside offcanvas) ── */}
                                {selectedFormat && (
                                    <div className="__preView_modal">
                                        <div className="__preView_modal_content">
                                            <div
                                                className="__preView_modal_header"
                                                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                            >
                                                <p style={{ fontWeight: 'bold' }}>{selectedFormat.name}</p>
                                                <button
                                                    onClick={() => setSelectedFormat(null)}
                                                    style={{ background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer' }}
                                                >
                                                    <CrossIcon />
                                                </button>
                                            </div>

                                            <div style={{ padding: '20px' }}>
                                                <img
                                                    src={selectedFormat.imageUrl}
                                                    alt={selectedFormat.name}
                                                    style={{ width: '100%', height: 'auto' }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Backdrop */}
            <div className={show ? 'offBack_drop' : ''} />
        </>
    );
};

export default AddLrFormatModal;